import { useIntl } from 'react-intl';
import { usePopupState, bindTrigger, bindPopover } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from 'components/mui/Tooltip';
import CountChip from 'components/AgGrid/components/TagsCellRenderer/CountChip';

import { FilterChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/StyledComponents';
import type { RenderChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/types';

interface OverflowChipsProps {
  hiddenChips: RenderChip[];
}

// The "+N" overflow control: a CountChip that opens a Popover listing the hidden
// chips, each individually removable (req: chip overflow collapses into a clickable
// count control). Renders nothing when all chips fit.
const OverflowChips = ({ hiddenChips }: OverflowChipsProps) => {
  const { formatMessage } = useIntl();
  const popupState = usePopupState({ variant: 'popover', popupId: 'workspaceSearchOverflow' });

  if (hiddenChips.length === 0) return null;

  return (
    <>
      <CountChip
        size="small"
        clickable
        label={hiddenChips.length}
        aria-label={formatMessage(
          { id: 'company.workspace.search.overflowCount', defaultMessage: '{count} more filters' },
          { count: hiddenChips.length },
        )}
        {...bindTrigger(popupState)}
      />
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack direction="row" flexWrap="wrap" gap={0.5} p={1} sx={{ maxWidth: (theme) => theme.spacing(45) }}>
          {hiddenChips.map((chip) => (
            <Tooltip key={chip.id} t={chip.title}>
              <FilterChip
                size="small"
                color="primary"
                variant="outlined"
                avatar={chip.avatar}
                icon={chip.icon}
                label={chip.label}
                onDelete={chip.onDelete}
              />
            </Tooltip>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default OverflowChips;
